<template>
  <HrPageLayout
    title="Skills audit report"
    subtitle=""
  >
    <div class="skills-audit-wrap">
      <div class="card hr-mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-2">
              <label class="form-label">Audit year</label>
              <select v-model.number="auditYear" class="form-select" @change="onYearChange">
                <option v-for="y in yearOptions" :key="'y-' + y" :value="y">{{ y }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">CSV file</label>
              <input
                ref="fileRef"
                type="file"
                accept=".csv,text/csv"
                class="form-control"
                @change="onFile"
              >
            </div>
            <div class="col-md-3">
              <button
                type="button"
                class="btn btn-primary"
                :disabled="uploading || !csvFile"
                @click="uploadCsv"
              >
                {{ uploading ? "Saving…" : "Save records" }}
              </button>
            </div>
          </div>
          <p v-if="uploadMsg" class="mt-2 mb-0" :class="uploadOk ? 'text-success' : 'text-danger'">{{ uploadMsg }}</p>
        </div>
      </div>

      <div class="card hr-mb-3">
        <div class="card-body">
          <h6 class="card-title">Summary (selected year)</h6>
          <p v-if="summaryLoading" class="text-muted mb-0">Loading…</p>
          <template v-else>
            <div class="row g-2 mb-3">
              <div class="col-md-4">
                <div class="border rounded p-2 bg-light">
                  <div class="small text-muted">All</div>
                  <div class="h5 mb-0">{{ complianceCountsSafe.totalRows }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="border rounded p-2" style="background:#ecfdf5;">
                  <div class="small text-muted">Compliant</div>
                  <div class="h5 mb-0 text-success">{{ complianceCountsSafe.compliantRows }}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="border rounded p-2" style="background:#fef2f2;">
                  <div class="small text-muted">Non-compliant</div>
                  <div class="h5 mb-0 text-danger">{{ complianceCountsSafe.nonCompliantRows }}</div>
                </div>
              </div>
            </div>
            <p class="mb-2"><strong>Total rows:</strong> {{ summary.totalRows }}</p>
            <div class="table-responsive" style="max-height: 220px; overflow: auto">
              <table class="table table-sm table-bordered mb-0">
                <thead><tr><th>Department</th><th>Rows</th></tr></thead>
                <tbody>
                  <tr v-for="(d, i) in summary.byDepartment" :key="'sd-' + i">
                    <td>{{ d.department || "—" }}</td>
                    <td>
                      Total: {{ Number(d.compliant_cnt || 0) + Number(d.non_compliant_cnt || 0) }}
                      | Compliant: {{ Number(d.compliant_cnt || 0) }}
                      | Non-compliant: {{ Number(d.non_compliant_cnt || 0) }}
                    </td>
                  </tr>
                  <tr v-if="!summary.byDepartment.length"><td colspan="2" class="text-muted">No data for this year.</td></tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <div class="d-flex flex-wrap gap-2 justify-content-between align-items-center hr-mb-2">
            <h6 class="card-title mb-0">Detail report</h6>
            <div class="d-flex flex-wrap gap-2">
              <select v-model="complianceTab" class="form-select form-select-sm" style="width: 180px" @change="loadReport(1)">
                <option value="all">All</option>
                <option value="compliant">Compliant</option>
                <option value="non-compliant">Non-compliant</option>
              </select>
              <select v-model="deptFilter" class="form-select form-select-sm" style="width: 240px" @change="loadReport(1)">
                <option value="">All departments</option>
                <option v-for="d in departmentOptions" :key="'dep-opt-' + d" :value="d">{{ d }}</option>
              </select>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="loadReport(1)">Apply</button>
              <button type="button" class="btn btn-sm btn-success" :disabled="!rows.length" @click="exportCsv">Export page CSV</button>
            </div>
          </div>
          <div class="table-responsive" style="max-height: 520px; overflow: auto">
            <table class="table table-sm table-striped table-bordered">
              <thead class="table-light sticky-top">
                <tr>
                  <th>Compliance</th>
                  <th>Name</th>
                  <th>Personal No</th>
                  <th>ID No</th>
                  <th>Phone</th>
                  <th>Dept</th>
                  <th>Designation (Appointment)</th>
                  <th>Designation (Deployment)</th>
                  <th>Skills</th>
                  <th>Duration</th>
                  <th>Evidence</th>
                  <th>Training Needs</th>
                  <th>Skills Gap Area</th>
                  <th>How am I doing</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading"><td colspan="14" class="text-muted">Loading…</td></tr>
                <tr v-else-if="!rows.length"><td colspan="14" class="text-muted">No rows. Upload a CSV or pick another year.</td></tr>
                <tr v-for="r in rows" :key="'r-' + r.id">
                  <td>{{ r.compliance_status || "compliant" }}</td>
                  <td>{{ r.employee_name }}</td>
                  <td>{{ r.personal_number }}</td>
                  <td>{{ r.national_id }}</td>
                  <td>{{ r.phone_number }}</td>
                  <td>{{ r.department }}</td>
                  <td>{{ r.designation_appointment }}</td>
                  <td>{{ r.designation_deployment }}</td>
                  <td class="text-truncate" style="max-width: 240px" :title="r.skill_description">{{ r.skill_description }}</td>
                  <td>{{ r.training_duration }}</td>
                  <td>{{ r.evidence_skills }}</td>
                  <td>{{ r.training_need }}</td>
                  <td>{{ r.skills_area }}</td>
                  <td>{{ r.how_am_i_doing }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="total > limit" class="d-flex justify-content-between align-items-center mt-2">
            <span class="small text-muted">Page {{ page }} — {{ total }} rows</span>
            <div>
              <button type="button" class="btn btn-sm btn-outline-primary me-1" :disabled="page <= 1 || loading" @click="loadReport(page - 1)">Prev</button>
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="page * limit >= total || loading" @click="loadReport(page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "SkillsAuditReport",
  components: { HrPageLayout },
  computed: {
    complianceCountsSafe() {
      const c = this.complianceCounts || {};
      return {
        totalRows: Number(c.totalRows || 0),
        compliantRows: Number(c.compliantRows || 0),
        nonCompliantRows: Number(c.nonCompliantRows || 0)
      };
    },
    departmentOptions() {
      const arr = Array.isArray(this.summary?.byDepartment) ? this.summary.byDepartment : [];
      return arr
        .map((d) => String(d.department || "").trim())
        .filter((d) => d !== "")
        .sort((a, b) => a.localeCompare(b));
    }
  },
  data() {
    const y = new Date().getFullYear();
    return {
      auditYear: y,
      yearOptions: [],
      csvFile: null,
      uploading: false,
      uploadMsg: "",
      uploadOk: false,
      loading: false,
      summaryLoading: false,
      deptFilter: "",
      complianceTab: "all",
      rows: [],
      complianceCounts: { totalRows: 0, compliantRows: 0, nonCompliantRows: 0 },
      total: 0,
      page: 1,
      limit: 50,
      summary: { totalRows: 0, byDepartment: [] }
    };
  },
  mounted() {
    this.loadYears();
  },
  methods: {
    onFile(e) {
      const f = e.target.files && e.target.files[0];
      this.csvFile = f || null;
      this.uploadMsg = "";
    },
    async onYearChange() {
      // Load grid first (user-visible), then aggregates — avoids hammering DB with 3 heavy queries at once on production.
      await this.loadReport(1);
      await Promise.all([this.loadSummary(), this.loadComplianceCounts()]);
    },
    async loadYears() {
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditYears");
        const res = await execute(fd, authUrl);
        const years = Array.isArray(res.data?.data?.years) ? res.data.data.years : [];
        const cur = new Date().getFullYear();
        // Always expose a broad selectable range even if DB has only one year.
        const rangeYears = [];
        for (let y = cur + 1; y >= cur - 20; y--) {
          rangeYears.push(y);
        }
        const set = new Set([...rangeYears, ...years.map((n) => Number(n))]);
        this.yearOptions = Array.from(set).filter((n) => n >= 1990 && n <= 2100).sort((a, b) => b - a);
        if (!this.yearOptions.includes(this.auditYear)) {
          this.yearOptions.unshift(this.auditYear);
        }
        await this.loadReport(1);
        await Promise.all([this.loadSummary(), this.loadComplianceCounts()]);
      } catch (_) {
        const cur = new Date().getFullYear();
        this.yearOptions = [];
        for (let y = cur + 1; y >= cur - 20; y--) {
          this.yearOptions.push(y);
        }
        await this.loadReport(1);
        await Promise.all([this.loadSummary(), this.loadComplianceCounts()]);
      }
    },
    async loadSummary() {
      this.summaryLoading = true;
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditSummary");
        fd.append("auditYear", String(this.auditYear));
        const res = await execute(fd, authUrl);
        if (res.data?.success && res.data.data) {
          this.summary = {
            totalRows: res.data.data.totalRows || 0,
            byDepartment: res.data.data.byDepartment || []
          };
        } else {
          this.summary = { totalRows: 0, byDepartment: [] };
        }
      } catch (_) {
        this.summary = { totalRows: 0, byDepartment: [] };
      } finally {
        this.summaryLoading = false;
      }
    },
    async loadComplianceCounts() {
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditComplianceCounts");
        fd.append("auditYear", String(this.auditYear));
        const res = await execute(fd, authUrl);
        if (res.data?.success && res.data.data) {
          this.complianceCounts = {
            totalRows: res.data.data.totalRows || 0,
            compliantRows: res.data.data.compliantRows || 0,
            nonCompliantRows: res.data.data.nonCompliantRows || 0
          };
        } else {
          this.complianceCounts = { totalRows: 0, compliantRows: 0, nonCompliantRows: 0 };
        }
      } catch (_) {
        this.complianceCounts = { totalRows: 0, compliantRows: 0, nonCompliantRows: 0 };
      }
    },
    async loadReport(p) {
      this.page = p;
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "listSkillsAuditReport");
        fd.append("auditYear", String(this.auditYear));
        fd.append("page", String(this.page));
        fd.append("limit", String(this.limit));
        fd.append("compliance", this.complianceTab);
        if (this.deptFilter.trim()) {
          fd.append("department", this.deptFilter.trim());
        }
        const res = await execute(fd, authUrl);
        if (res.data?.success && res.data.data) {
          this.rows = res.data.data.rows || [];
          this.total = res.data.data.total || 0;
        } else {
          this.rows = [];
          this.total = 0;
        }
      } catch (_) {
        this.rows = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    async uploadCsv() {
      if (!this.csvFile) return;
      this.uploading = true;
      this.uploadMsg = "";
      try {
        const fd = new FormData();
        fd.append("function", "uploadSkillsAuditBulkCsv");
        fd.append("auditYear", String(this.auditYear));
        fd.append("skillsCsv", this.csvFile);
        const res = await execute(fd, authUrl);
        this.uploadOk = !!(res.data && res.data.success);
        this.uploadMsg = (res.data && res.data.message) || (this.uploadOk ? "Saved." : "Upload failed.");
        if (this.uploadOk) {
          const inserted = Number(res?.data?.data?.inserted || 0);
          this.uploadMsg = `Saved ${inserted} record(s) for audit year ${this.auditYear}. You can now select this year to view the report anytime.`;
          if (this.$refs.fileRef) this.$refs.fileRef.value = "";
          this.csvFile = null;
          await this.loadYears();
        }
      } catch (e) {
        this.uploadOk = false;
        this.uploadMsg = e.message || "Upload failed.";
      } finally {
        this.uploading = false;
      }
    },
    exportCsv() {
      const cols = [
        "employee_name",
        "national_id",
        "personal_number",
        "phone_number",
        "department",
        "designation_appointment",
        "designation_deployment",
        "skill_description",
        "training_duration",
        "evidence_skills",
        "training_need",
        "skills_area",
        "how_am_i_doing",
        "created_at"
      ];
      const esc = (v) => {
        const s = v == null ? "" : String(v);
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const lines = [cols.join(",")];
      this.rows.forEach((r) => {
        lines.push(cols.map((c) => esc(r[c])).join(","));
      });
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `skills-audit-${this.auditYear}-page${this.page}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.skills-audit-wrap {
  width: 100%;
  max-width: 100%;
}
.hr-mb-2 {
  margin-bottom: 0.5rem;
}
.hr-mb-3 {
  margin-bottom: 1rem;
}
</style>
